import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { FaiscasChoqueEletrico3D, Fogo3D } from '../componentes/FogoEFaiscasChoque3D'
import type { AparenciaRisco } from '../../dominio/tabletop/atendimento/riscos'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { CaminhaoBombeiros3D } from './viaturas/CaminhaoBombeiros3D'
import { Bombeiro3D, BombeiroMedico3D } from './Bombeiros3D'
import { CortadorHolmatro3D } from '../oficina/CortadorHolmatro3D'
import { AlargadorHolmatro3D } from '../oficina/AlargadorHolmatro3D'

/**
 * 1. Caminhão de Bombeiros — Auto Bomba Tanque / Salvamento (ABT)
 *
 * Implementa a viatura pesada de proteção rodoviária:
 * - Corpo em vermelho bombeiro (#dc2626) com detalhes e faixas refletivas
 * - Cabine dupla (crew cab) com para-brisa, janelas fumê e grade dianteira
 * - Para-choque reforçado em aço escuro com guincho mecânico
 * - Baú de ferramentas/equipamentos com persianas de enrolar de alumínio anodizado
 * - Escada de resgate e canhão monitor/mangotinho no teto
 * - Traseira com faixas chevron refletivas de segurança viária (45° amarelo e vermelho)
 * - Rodas dianteiras esterçadas para fora da pista (doutrina operacional: deflexão segura)
 * - Giroflex com barra de iluminação de emergência (LEDs estroboscópicos pulsantes)
 */
export function CaminhaoBombeirosTabletop() {
  return <CaminhaoBombeiros3D />
}

/**
 * 2. Bombeiro com Roupa de Combate a Incêndio / Salvamento
 *
 * Características solicitadas:
 * - Capa e calça em tecido ripstop aramida cáqui-dourado (#a16207 / #b45309)
 * - Faixas refletivas duplas de alta visibilidade (amarelo e prata)
 * - Capacete de bombeiro com casco vermelho e visor protetor
 * - Luvas de salvamento veicular de alta aderência
 * - Botas pretas de combate a incêndio
 * - SEM proteção respiratória autônoma (EPR/cilindro) nas costas
 */
export function BombeiroTabletop() {
  return <Bombeiro3D />
}

/**
 * 3. Bombeiro Médico / Resgatista de APH
 *
 * Características solicitadas:
 * - Roupa de bombeiro (capa de incêndio/resgate) com faixas refletivas
 * - Capacete de bombeiro branco (padrão de comando e saúde) com cruz médica
 * - Mão direita: Bolsa de primeiros socorros vermelha com cruz branca (Kit Trauma)
 * - Mão esquerda: Cilindro portátil de Oxigênio (O2) medicinal verde com manômetro
 */
export function BombeiroMedicoTabletop() {
  return <BombeiroMedico3D />
}

/**
 * 4. Espaço Livre para a USA (Unidade de Suporte Avançado / Ambulância Médica)
 *
 * Plano tático demarcado no asfalto:
 * - Área de 3.6m x 8.0m (dimensão segura para manobra da ambulância)
 * - Bordas tracejadas de segurança em amarelo e branco
 * - Símbolo de primeiros socorros / Estrela da Vida
 * - Identificação operacional: "ÁREA LIVRE — CHEGADA USA (SUPORTE AVANÇADO)"
 */
export function AreaUsaTabletop() {
  const noRaycast = () => null

  return (
    <group position={[0, 0.003, 0]}>
      {/* Área demarcada no solo com leve opacidade */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
        <planeGeometry args={[3.6, 7.8]} />
        <meshBasicMaterial color="#0284c7" transparent opacity={0.12} depthWrite={false} side={2} />
      </mesh>

      {/* Borda tracejada perimetral */}
      {/* Linhas longitudinais */}
      {[-1.8, 1.8].map((x) => (
        <mesh key={x} position={[x, 0.001, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
          <planeGeometry args={[0.1, 7.8]} />
          <meshBasicMaterial color="#facc15" transparent opacity={0.85} depthWrite={false} side={2} />
        </mesh>
      ))}

      {/* Linhas transversais */}
      {[-3.9, 3.9].map((z) => (
        <mesh key={z} position={[0, 0.001, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
          <planeGeometry args={[3.6, 0.1]} />
          <meshBasicMaterial color="#facc15" transparent opacity={0.85} depthWrite={false} side={2} />
        </mesh>
      ))}

      {/* Cruz Médica / Estrela no centro */}
      <group position={[0, 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <mesh raycast={noRaycast}>
          <planeGeometry args={[0.4, 1.4]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.9} depthWrite={false} side={2} />
        </mesh>
        <mesh raycast={noRaycast}>
          <planeGeometry args={[1.4, 0.4]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.9} depthWrite={false} side={2} />
        </mesh>
      </group>
    </group>
  )
}

/**
 * 5. Mancha de Sangue no Solo / Veículo (Risco Biológico e Hemorragia Externa)
 *
 * Simula uma poça líquida orgânica de sangue coagulado:
 * - Geometria de múltiplos lóbulos orgânicos concêntricos e excêntricos
 * - Vermelho profundo coagulado (#3b0202, #4a0404 e #7f1d1d)
 * - Brilho úmido especular (roughness 0.06, metalness 0.2)
 * - Gotas satélites de respingo ao redor do contorno
 */
export function ManchaSangueTabletop({
  aparencia,
}: {
  aparencia?: { areaIsolada?: boolean }
}) {
  const noRaycast = () => null

  return (
    <group position={[0, 0.012, 0]}>
      {/* Poça central principal espessa */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} scale={[1, 0.78, 1]} raycast={noRaycast}>
        <circleGeometry args={[0.55, 48]} />
        <meshStandardMaterial color="#3b0202" roughness={0.06} metalness={0.2} />
      </mesh>

      {/* Lóbulo orgânico secundário esquerdo */}
      <mesh position={[-0.22, 0.001, 0.15]} rotation={[-Math.PI / 2, 0, 0.4]} scale={[0.42, 0.32, 1]} raycast={noRaycast}>
        <circleGeometry args={[0.5, 32]} />
        <meshStandardMaterial color="#4a0404" roughness={0.07} metalness={0.15} />
      </mesh>

      {/* Lóbulo orgânico secundário direito */}
      <mesh position={[0.26, 0.001, -0.12]} rotation={[-Math.PI / 2, 0, -0.6]} scale={[0.48, 0.36, 1]} raycast={noRaycast}>
        <circleGeometry args={[0.5, 32]} />
        <meshStandardMaterial color="#4a0404" roughness={0.07} metalness={0.15} />
      </mesh>

      {/* Camada líquida translúcida carmesim superior (brilho de fluido fresco nas bordas) */}
      <mesh position={[0, 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1.05, 0.82, 1]} raycast={noRaycast}>
        <ringGeometry args={[0.3, 0.58, 48]} />
        <meshStandardMaterial
          color="#7f1d1d"
          emissive="#450a0a"
          emissiveIntensity={0.25}
          roughness={0.08}
          metalness={0.2}
          transparent
          opacity={0.88}
          depthWrite={false}
        />
      </mesh>

      {/* Gotas e respingos satélites em volta da poça */}
      {[
        [-0.58, 0.28, 0.06],
        [-0.45, -0.38, 0.045],
        [0.52, 0.32, 0.05],
        [0.64, -0.18, 0.055],
        [0.12, 0.52, 0.04],
        [-0.18, -0.48, 0.04],
        [0.35, -0.42, 0.035],
      ].map(([x, z, r], i) => (
        <mesh key={i} position={[x, 0.001, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
          <circleGeometry args={[r, 16]} />
          <meshStandardMaterial color="#450a0a" roughness={0.06} metalness={0.1} />
        </mesh>
      ))}

      {aparencia?.areaIsolada && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.003, 0]}>
          <ringGeometry args={[1.0, 1.06, 32]} />
          <meshBasicMaterial color="#facc15" side={2} />
        </mesh>
      )}

      {/* Área interativa de toque */}
      <mesh visible={false} position={[0, 0.1, 0]}>
        <boxGeometry args={[1.3, 0.2, 1.1]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

/**
 * 6. Extintor de Incêndio PQS (Pó Químico Seco 4kg/6kg)
 *
 * Equipamento indispensável de segurança e linha de prontidão em salvamento veicular:
 * - Cilindro vermelho bombeiro (#dc2626) com base antiderrapante em polímero escuro
 * - Cabeçote de válvula metálico em latão/aço escuro com manômetro indicador
 * - Gatilho de acionamento e alça de transporte
 * - Mangueira de descarga flexível de borracha com bico difusor cônico
 * - Faixa de instruções de operação em branco
 */
export function ExtintorPqsTabletop() {
  return (
    <group position={[0, 0.28, 0]}>
      {/* Base / saia protetora do fundo */}
      <mesh position={[0, -0.25, 0]}>
        <cylinderGeometry args={[0.092, 0.095, 0.06, 24]} />
        <meshStandardMaterial color="#0f172a" roughness={0.8} />
      </mesh>

      {/* Cilindro principal pressurizado em vermelho vivo */}
      <mesh position={[0, -0.04, 0]}>
        <cylinderGeometry args={[0.09, 0.09, 0.42, 24]} />
        <meshStandardMaterial color="#dc2626" roughness={0.32} metalness={0.15} />
      </mesh>

      {/* Cúpula superior do cilindro */}
      <mesh position={[0, 0.17, 0]}>
        <sphereGeometry args={[0.09, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#dc2626" roughness={0.32} metalness={0.15} />
      </mesh>

      {/* Rótulo informativo no corpo do cilindro */}
      <mesh position={[0, -0.04, 0]}>
        <cylinderGeometry args={[0.091, 0.091, 0.16, 24]} />
        <meshStandardMaterial color="#f8fafc" roughness={0.5} />
      </mesh>

      {/* Gargalo e válvula em latão/aço */}
      <mesh position={[0, 0.23, 0]}>
        <cylinderGeometry args={[0.024, 0.03, 0.06, 16]} />
        <meshStandardMaterial color="#475569" metalness={0.8} roughness={0.3} />
      </mesh>

      {/* Alça de transporte fixa */}
      <mesh position={[-0.04, 0.26, 0]} rotation={[0, 0, -0.2]}>
        <CaixaArredondada args={[0.1, 0.015, 0.025]} />
        <meshStandardMaterial color="#1e293b" metalness={0.7} />
      </mesh>

      {/* Gatilho móvel superior de descarga */}
      <mesh position={[-0.04, 0.29, 0]} rotation={[0, 0, 0.15]}>
        <CaixaArredondada args={[0.11, 0.012, 0.022]} />
        <meshStandardMaterial color="#1e293b" metalness={0.7} />
      </mesh>

      {/* Manômetro diminuto de pressão */}
      <mesh position={[0.035, 0.245, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.018, 0.018, 0.015, 16]} />
        <meshStandardMaterial color="#e2e8f0" metalness={0.3} roughness={0.3} />
      </mesh>

      {/* Mangueira de descarga lateral */}
      <mesh position={[0.085, 0.04, 0]}>
        <cylinderGeometry args={[0.01, 0.01, 0.36, 12]} />
        <meshStandardMaterial color="#0f172a" roughness={0.9} />
      </mesh>

      {/* Bico difusor cônico no final da mangueira */}
      <mesh position={[0.085, -0.16, 0]} rotation={[Math.PI, 0, 0]}>
        <cylinderGeometry args={[0.018, 0.008, 0.06, 16]} />
        <meshStandardMaterial color="#0f172a" roughness={0.8} />
      </mesh>

      {/* Área de toque */}
      <mesh visible={false}>
        <boxGeometry args={[0.3, 0.65, 0.3]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

/**
 * 7. Mochila de Trauma e Resgate APH (Atendimento Pré-Hospitalar)
 *
 * Utilizada pela equipe de resgate / socorristas médicos:
 * - Corpo em lona de cordura vermelha de alta resistência (#b91c1c)
 * - Bolsos frontais e laterais modulares com zíperes
 * - Cruz da Vida em branco centralizada
 * - Faixas refletivas amarelas de alta visibilidade
 * - Alça de transporte superior robusta
 */
export function MochilaTraumaTabletop() {
  return (
    <group position={[0, 0.21, 0]}>
      {/* Corpo principal da mochila */}
      <mesh position={[0, 0, 0]}>
        <CaixaArredondada args={[0.34, 0.42, 0.24]} />
        <meshStandardMaterial color="#b91c1c" roughness={0.65} />
      </mesh>

      {/* Faixas refletivas amarelas horizontais */}
      {[-0.08, 0.12].map((y) => (
        <mesh key={y} position={[0, y, 0.122]}>
          <planeGeometry args={[0.32, 0.03]} />
          <meshStandardMaterial color="#facc15" emissive="#facc15" emissiveIntensity={0.2} roughness={0.3} />
        </mesh>
      ))}

      {/* Compartimento frontal protuberante */}
      <mesh position={[0, 0.02, 0.14]}>
        <CaixaArredondada args={[0.26, 0.28, 0.06]} />
        <meshStandardMaterial color="#991b1b" roughness={0.7} />
      </mesh>

      {/* Cruz de Emergência branca no bolso frontal */}
      <group position={[0, 0.02, 0.172]}>
        <mesh>
          <planeGeometry args={[0.04, 0.14]} />
          <meshBasicMaterial color="#ffffff" side={2} />
        </mesh>
        <mesh>
          <planeGeometry args={[0.14, 0.04]} />
          <meshBasicMaterial color="#ffffff" side={2} />
        </mesh>
      </group>

      {/* Bolsos laterais para kits rápidos de gaze/torniquete */}
      {[-1, 1].map((lado) => (
        <mesh key={lado} position={[lado * 0.185, -0.03, 0]}>
          <CaixaArredondada args={[0.04, 0.26, 0.18]} />
          <meshStandardMaterial color="#991b1b" roughness={0.7} />
        </mesh>
      ))}

      {/* Alça superior reforçada de transporte manual */}
      <mesh position={[0, 0.23, 0]}>
        <CaixaArredondada args={[0.12, 0.04, 0.03]} />
        <meshStandardMaterial color="#1e293b" roughness={0.8} />
      </mesh>

      {/* Fundo emborrachado preto reforçado para apoio no solo úmido/asfalto */}
      <mesh position={[0, -0.21, 0]}>
        <CaixaArredondada args={[0.35, 0.02, 0.25]} />
        <meshStandardMaterial color="#0f172a" roughness={0.9} />
      </mesh>

      {/* Área de toque */}
      <mesh visible={false}>
        <boxGeometry args={[0.42, 0.5, 0.35]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

/**
 * 8. Lona de Ferramentas — Tool Staging Area (Resgate Veicular Moderno)
 *
 * Conforme a doutrina operacional moderna:
 * - Lona vinílica retangular em Laranja Resgate (#ea580c) estendida no solo (2.4m x 1.6m)
 * - Moldura perimetral preta reforçada com ilhoses
 * - Equipamentos modernos a bateria (sem motobomba a combustão nem mangueiras pesadas):
 *   1. Cortador / Desencarcerador a bateria (bloco ergonômico + bateria + lâminas de corte curvas)
 *   2. Alargador / Expansor a bateria (braços em V abertos com pontas dentadas)
 *   3. Conjunto de estabilização (calços escalonados de madeira e bloco de cribbing)
 *   4. Prancha longa rígida amarela de resgate com imobilizadores
 */
export function LonaFerramentasTabletop() {
  const noRaycast = () => null

  return (
    <group position={[0, 0.004, 0]}>
      {/* Lona vinílica principal no solo em Laranja Resgate */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
        <planeGeometry args={[2.4, 1.6]} />
        <meshStandardMaterial color="#ea580c" roughness={0.65} metalness={0.05} side={2} />
      </mesh>

      {/* Moldura / borda reforçada preta da lona */}
      {[-1.2, 1.2].map((x) => (
        <mesh key={x} position={[x, 0.001, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
          <planeGeometry args={[0.06, 1.6]} />
          <meshBasicMaterial color="#1e293b" side={2} />
        </mesh>
      ))}
      {[-0.8, 0.8].map((z) => (
        <mesh key={z} position={[0, 0.001, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
          <planeGeometry args={[2.4, 0.06]} />
          <meshBasicMaterial color="#1e293b" side={2} />
        </mesh>
      ))}

      {/* ITENS 1 e 2: cortador e alargador a bateria — os mesmos modelos da
          Oficina de corte, deitados sobre a lona com o perfil para cima */}
      <group position={[-0.68, 0.075, -0.45]} rotation={[-Math.PI / 2, 0, 0]}>
        <CortadorHolmatro3D anguloMandibula={0.05} />
      </group>
      <group position={[-0.55, 0.085, 0.4]} rotation={[-Math.PI / 2, 0, 0]}>
        <AlargadorHolmatro3D />
      </group>

      {/* ITEM 3: Equipamentos de Estabilização (Calços e Blocos) */}
      <group position={[0.22, 0.06, -0.35]}>
        <mesh position={[0, 0.04, 0]}>
          <CaixaArredondada args={[0.42, 0.08, 0.14]} />
          <meshStandardMaterial color="#b45309" roughness={0.85} />
        </mesh>
        <mesh position={[0.06, 0.1, 0]}>
          <CaixaArredondada args={[0.28, 0.06, 0.14]} />
          <meshStandardMaterial color="#b45309" roughness={0.85} />
        </mesh>
        <mesh position={[0.12, 0.15, 0]}>
          <CaixaArredondada args={[0.14, 0.05, 0.14]} />
          <meshStandardMaterial color="#b45309" roughness={0.85} />
        </mesh>
        <mesh position={[0, 0.05, 0.22]}>
          <CaixaArredondada args={[0.35, 0.09, 0.1]} />
          <meshStandardMaterial color="#92400e" roughness={0.9} />
        </mesh>
        <mesh position={[0, 0.14, 0.22]} rotation={[0, Math.PI / 2, 0]}>
          <CaixaArredondada args={[0.35, 0.09, 0.1]} />
          <meshStandardMaterial color="#92400e" roughness={0.9} />
        </mesh>
      </group>

      {/* ITEM 4: Prancha Longa Rígida de Coluna */}
      <group position={[0.78, 0.04, 0]}>
        <mesh position={[0, 0, 0]}>
          <CaixaArredondada args={[0.45, 0.045, 1.45]} />
          <meshStandardMaterial color="#facc15" roughness={0.35} />
        </mesh>
        {[-0.14, 0.14].map((x) => (
          <mesh key={x} position={[x, 0.06, -0.55]}>
            <CaixaArredondada args={[0.09, 0.08, 0.16]} />
            <meshStandardMaterial color="#dc2626" roughness={0.7} />
          </mesh>
        ))}
        {[-0.2, 0.05, 0.35].map((z) => (
          <mesh key={z} position={[0, 0.026, z]}>
            <CaixaArredondada args={[0.46, 0.01, 0.05]} />
            <meshStandardMaterial color="#0f172a" roughness={0.9} />
          </mesh>
        ))}
      </group>

      {/* Área interativa para clique/toque de seleção */}
      <mesh visible={false} position={[0, 0.15, 0]}>
        <boxGeometry args={[2.5, 0.35, 1.7]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

/**
 * 9. Poste de Rede Elétrica com Fio Caído, Fogo e Faíscas de Choque
 *
 * Simula um poste de concreto de média tensão atingido por colisão ou danificado:
 * - Fuste de concreto cônico com sapata de base e faixas refletivas
 * - Cruzeta de madeira tratada/aço com braçadeiras e mão-francesa de reforço
 * - 3 Isoladores cerâmicos tipo campânula vitrificada
 * - Fiação aérea contínua nos isoladores intactos
 * - Fio elétrico partido/caído que desce em curva catenária até o solo
 * - Efeito dinâmico de chamas (fogo de curto-circuito) e faíscas de choque (arcos voltaicos azuis/brancos)
 *   no ponto de contato com o solo, com iluminação pulsante e mancha de asfalto queimado.
 */
export function PosteTabletop() {
  const curvaFio = useMemo(
    () =>
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(0.65, 4.28, 0),
        new THREE.Vector3(0.78, 3.2, 0.18),
        new THREE.Vector3(0.88, 1.85, 0.42),
        new THREE.Vector3(0.98, 0.65, 0.68),
        new THREE.Vector3(1.12, 0.04, 0.88),
        new THREE.Vector3(1.28, 0.05, 1.05),
      ]),
    [],
  )

  return (
    <group aria-label="Poste de rede elétrica com fio caído, fogo e faíscas de choque">
      {/* Base / Sapata de fundação do poste no solo */}
      <mesh position={[0, 0.08, 0]}>
        <cylinderGeometry args={[0.26, 0.28, 0.16, 16]} />
        <meshStandardMaterial color="#52525b" roughness={0.95} />
      </mesh>

      {/* Fuste cônico principal de concreto armado */}
      <mesh position={[0, 2.25, 0]} castShadow>
        <cylinderGeometry args={[0.13, 0.22, 4.5, 16]} />
        <meshStandardMaterial color="#73777a" roughness={0.92} />
      </mesh>

      {/* Faixas refletivas de identificação viária amarela no fuste */}
      <mesh position={[0, 1.35, 0]}>
        <cylinderGeometry args={[0.19, 0.19, 0.12, 16]} />
        <meshStandardMaterial
          color="#facc15"
          emissive="#ca8a04"
          emissiveIntensity={0.25}
          roughness={0.5}
        />
      </mesh>

      {/* Cruzeta superior de distribuição de energia */}
      <mesh position={[0, 4.15, 0]}>
        <CaixaArredondada args={[1.7, 0.14, 0.14]} />
        <meshStandardMaterial color="#334155" roughness={0.8} />
      </mesh>

      {/* Mão-francesa de reforço estrutural em V sob a cruzeta */}
      {[-0.45, 0.45].map((x) => (
        <mesh
          key={x}
          position={[x / 2, 3.82, 0]}
          rotation={[0, 0, x > 0 ? -0.55 : 0.55]}
        >
          <CaixaArredondada args={[0.04, 0.72, 0.03]} />
          <meshStandardMaterial color="#64748b" metalness={0.7} roughness={0.4} />
        </mesh>
      ))}

      {/* 3 Isoladores cerâmicos tipo campânula vitrificada */}
      {[-0.65, 0, 0.65].map((x, idx) => (
        <group key={idx} position={[x, 4.28, 0]}>
          <mesh position={[0, 0, 0]}>
            <cylinderGeometry args={[0.035, 0.065, 0.14, 8]} />
            <meshStandardMaterial color="#78350f" roughness={0.3} metalness={0.1} />
          </mesh>
          <mesh position={[0, 0.08, 0]}>
            <cylinderGeometry args={[0.015, 0.015, 0.06, 6]} />
            <meshStandardMaterial color="#94a3b8" metalness={0.8} roughness={0.3} />
          </mesh>
        </group>
      ))}

      {/* Fiação aérea contínua passando pelos isoladores intactos (esquerdo e central) */}
      <mesh position={[-0.65, 4.36, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.014, 0.014, 4.5, 6]} />
        <meshStandardMaterial color="#1e293b" roughness={0.7} />
      </mesh>
      <mesh position={[0, 4.36, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.014, 0.014, 4.5, 6]} />
        <meshStandardMaterial color="#1e293b" roughness={0.7} />
      </mesh>

      {/* Fio de alta tensão partido e caído até o chão */}
      <mesh>
        <tubeGeometry args={[curvaFio, 32, 0.02, 8, false]} />
        <meshStandardMaterial color="#1e293b" roughness={0.7} />
      </mesh>

      {/* Ponta desencapada do fio de cobre fundindo no solo */}
      <mesh position={[1.28, 0.05, 1.05]}>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial
          color="#f59e0b"
          emissive="#f59e0b"
          emissiveIntensity={3.5}
        />
      </mesh>

      {/* FAÍSCAS ELÉTRICAS DE CHOQUE NO FIO DO POSTE: */}
      {/* 1. Faíscas elétricas de curto no isolador rompido no topo do poste */}
      <FaiscasChoqueEletrico3D
        posicao={[0.65, 4.28, 0]}
        escala={0.75}
        ativo={true}
      />

      {/* 2. Faíscas e arcos voltaicos de choque elétrico no ponto de contato com o solo */}
      <FaiscasChoqueEletrico3D
        posicao={[1.28, 0, 1.05]}
        escala={1.2}
        ativo={true}
      />

      {/* Área interativa para clique/toque de seleção */}
      <mesh visible={false} position={[0, 2.25, 0]}>
        <cylinderGeometry args={[0.45, 0.55, 4.6, 8]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

/**
 * 10. Fogo / Princípio de Incêndio (Elemento Tático Independente)
 *
 * Elemento tático para ser posicionado livremente no veículo (capô, motor,
 * habitáculo, sob o assoalho) ou em qualquer ponto da via pelo instrutor:
 * - Chamas volumosas multicamadas com línguas de fogo dançantes
 * - Coluna de fumaça cinza-escura por convecção
 * - Brasas incandescentes ascendentes
 * - Iluminação dinâmica quente
 * - Solo chamuscado quando ativo ou cinzas apagadas quando extinto
 */
export function FogoTabletop({ aparencia }: { aparencia?: AparenciaRisco }) {
  const extinto = aparencia?.extinto ?? false
  return (
    <group aria-label="Fogo ou princípio de incêndio">
      <Fogo3D ativo={!extinto} escala={1.25} />
      <mesh visible={false} position={[0, 0.4, 0]}>
        <boxGeometry args={[1.2, 1.0, 1.2]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

